import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAnnotations } from './useAnnotations';
import { svgToPng } from '../../utils/svg';

interface UseAnnotationCursorProps {
  canAddAnnotation: boolean;
}

export const useAnnotationCursor = ({ canAddAnnotation }: UseAnnotationCursorProps) => {
  const annotationCursor = useRef<string>('');
  const hasActiveAnnotationRef = useRef(false);

  const {
    activeAnnotation,
    activeCriterion,
    activeTask,
    addingAnnotationEnabled,
    canvas,
    cursorDimensions,
    isActiveAnnotationForGrading,
    defaultAnnotationSizeRef,
  } = useAnnotations();

  const isAnnotationCursorVisible = useMemo(() => {
    return (
      activeAnnotation &&
      activeCriterion &&
      ((activeCriterion.isScoringAnnotationRequired && isActiveAnnotationForGrading) ||
        !isActiveAnnotationForGrading ||
        (!activeCriterion.isScoringAnnotationRequired && !isActiveAnnotationForGrading)) &&
      activeTask &&
      addingAnnotationEnabled &&
      canAddAnnotation
    );
  }, [
    activeAnnotation,
    activeCriterion,
    activeCriterion?.isScoringAnnotationRequired,
    isActiveAnnotationForGrading,
    activeTask,
    addingAnnotationEnabled,
    canAddAnnotation,
  ]);

  const setCurrentCursor = (forceDefault = false) => {
    const defaultCursor = 'pointer';
    if (!forceDefault && isAnnotationCursorVisible) {
      if (!hasActiveAnnotationRef.current) {
        console.warn('No active annotation, skipping setting up canvas cursor');
        return;
      }

      if (canvas.defaultCursor === annotationCursor.current) {
        return;
      }

      canvas.defaultCursor = annotationCursor.current;
      canvas.hoverCursor = annotationCursor.current;
      canvas.setCursor(annotationCursor.current);
      canvas.moveCursor = 'move';
    } else {
      canvas.defaultCursor = defaultCursor;
      canvas.hoverCursor = defaultCursor;
      canvas.setCursor(defaultCursor);
      canvas.moveCursor = 'move';
    }
  };

  const setCursorFromSvg = useCallback(async () => {
    if (!activeAnnotation) {
      return;
    }
    try {
      const svgData = await svgToPng(activeAnnotation.svg, 0, true);
      if (activeAnnotation && svgData) {
        const annotationCursorString = `url(${svgData.dataUrl}) ${Math.round(svgData.width / 2)} ${
          svgData.height / 2
        }, auto`;
        annotationCursor.current = annotationCursorString;
        defaultAnnotationSizeRef.current = { width: svgData.width, height: svgData.height };
        cursorDimensions.current = { width: svgData.width, height: svgData.height };
        setCurrentCursor();
      }
    } catch {
      console.log('error creating cursor');
    }
  }, [activeAnnotation, isAnnotationCursorVisible]);

  useEffect(() => {
    if (!canvas) return;
    if (isAnnotationCursorVisible) {
      setCursorFromSvg();
    } else {
      annotationCursor.current = '';
      cursorDimensions.current = null;
      setCurrentCursor();
    }
  }, [isAnnotationCursorVisible, setCursorFromSvg]);

  useEffect(() => {
    hasActiveAnnotationRef.current = !!activeAnnotation;
    if (!activeAnnotation) {
      setCurrentCursor(true);
    }
  }, [activeAnnotation]);

  return { setCurrentCursor };
};
